<template>
  <div v-if="quantityRemaining >= count" class="relative inline-flex -mr-2">
    <div class="flex flex-col">
      <div v-if="soldOut" class="flex">
        <button
          class="inline-block w-24 h-8 ml-1 text-gray-brand-700 text-xs font-medium bg-gray-brand-300 rounded-md tracking-tight"
          :disabled="true"
        >
          Sold Out
        </button>
      </div>
      <div v-else class="flex">
        <button
          v-if="count > 0"
          class="transition-all inline-block w-8 h-8 p-2 mr-1 rounded-lg items-center text-gray-brand-500 hover:text-gray-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-800"
          @click="decreaseCount()"
        >
          <svg
            v-if="count - step >= 0"
            class="w-full fill-current"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M20 12H4"></path>
          </svg>
          <TrashIcon v-else class="w-full fill-current" />
        </button>
        <div className="flex rounded-md shadow-sm group">
          <input
            type="text"
            inputmode="numeric"
            placeholder="0"
            class="transition-all peer w-8 h-8 text-center md:hover:border-gray-brand-600 border rounded-l-lg font-inter font-semibold focus:outline-none focus:ring-2 focus:ring-brand-800"
            :value="countForValue"
            :class="[
              count > 0
                ? 'bg-gray-brand-600 text-gray-brand-100 border-gray-brand-500'
                : 'bg-gray-brand-200 text-gray-brand-500 border-none',
              hasUniqueName ? '' : 'rounded-r-lg'
            ]"
            @change="setQuantity($event.target.value)"
          />
          <span
            v-if="hasUniqueName"
            class="transition-all inline-flex items-center rounded-r-lg border-l border-gray-300 px-2.5 text-gray-500 sm:text-sm peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-brand-800"
            :class="[
              count > 0
                ? 'bg-gray-brand-600 text-gray-brand-100 border-gray-brand-500'
                : 'bg-gray-brand-200 text-gray-brand-600'
            ]"
          >
            {{ product.quantity_name }}
          </span>
        </div>
        <button
          v-if="noneLeft"
          class="transition-all inline-block w-24 h-8 ml-1 text-gray-brand-700 text-xs font-medium bg-gray-brand-300 rounded-md tracking-tight"
          :disabled="true"
        >
          Max Available
        </button>
        <button
          v-else
          class="transition-all inline-flex ml-1 p-2 rounded-lg items-center text-gray-brand-500 hover:text-gray-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-800"
          :disabled="noneLeft"
          @click="increaseCount()"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="4" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
  <div v-else-if="!isActivelySubmittingCart" class="relative inline-flex -mr-2">
    <button
      class="transition-all h-8 p-2 px-3 mr-1 rounded-lg inline-flex items-center text-sm text-white bg-gray-brand-600 font-bold hover:text-gray-brand-500"
      @click="set(0)"
    >
      Remove {{ count }} item{{ count > 1 ? 's' : '' }} (sold out)
    </button>
  </div>
  <div v-else-if="isActivelySubmittingCart" class="relative inline-flex -mr-2">
    <label
      class="transition-all ml-2 mr-1 select-none text-sm cursor-pointer font-semibold tracking-tighter text-gray-brand-700"
    >
      Updating cart...
    </label>
  </div>
</template>

<script>
import { inject } from 'vue'
import { mapGetters } from 'vuex'
import { TrashIcon } from '@heroicons/vue/solid'
import { posthog, getCommonTrackingProperties } from '@/composables/usePosthog'

export default {
  name: 'V2ProductQuantity',

  components: { TrashIcon },

  props: {
    product: {
      type: Object,
      required: true
    },
    submittableCartItem: {
      type: Object,
      default: null
    }
  },

  setup() {
    const v2UpdateCartItem = inject('v2UpdateCartItem', null)
    const v2IsSubmittingCart = inject('v2IsSubmittingCart', null)
    return { v2UpdateCartItem, v2IsSubmittingCart }
  },

  computed: {
    ...mapGetters(['info']),
    countForValue() {
      return this.count === 0 ? null : this.count
    },
    count() {
      return this.submittableCartItem?.quantity || 0
    },
    isActivelySubmittingCart() {
      if (this.v2IsSubmittingCart != null) return this.v2IsSubmittingCart
      return this.$store.state.activelySubmittingCart
    },
    inventory() {
      const item = this.submittableCartItem || {}
      return {
        product: item.product_inventory_remaining ?? this.product?.product_inventory_remaining,
        store: item.store_inventory_remaining ?? this.product?.store_inventory_remaining
      }
    },
    quantityRemaining() {
      return Math.min(this.inventory.store ?? 999999, this.inventory.product ?? 999999) + this.count
    },
    maxQuantity() {
      return Math.min(this.inventory.store ?? Infinity, this.inventory.product ?? Infinity)
    },
    soldOut() {
      return this.count === 0 && this.quantityRemaining <= 0
    },
    noneLeft() {
      // No more can be added: the product is out of stock, or the cart is
      // exactly at the available cap. When count > maxQuantity (a cart that
      // pre-dates an inventory drop) we still show the + button — clicking it
      // clamps the quantity down to the cap (see increaseCount/setQuantity),
      // matching v1's server-side clamp; the next render then has
      // count === maxQuantity and the "Max Available" pill appears.
      return this.maxQuantity <= 0 || this.count === this.maxQuantity
    },
    hasUniqueName() {
      return !!this.product.quantity_name
    },
    step() {
      return this.product.quantity_step || 1
    },
    facebookPixelContainerID() {
      return this.info?.attributes?.facebook_pixel_container_id
    }
  },

  methods: {
    decreaseCount() {
      if (this.count === 0) return
      const newCount = this.count - this.step < 1 ? 0 : this.count - this.step
      this.set(newCount)

      posthog.capture('removed_incremental_cart_item', {
        ...getCommonTrackingProperties(this),
        count: newCount
      })
    },
    increaseCount() {
      // No client-side clamp — the cart endpoint clamps the submitted quantity
      // to available inventory (mirrors v1) and the response syncs back. The +
      // button is already hidden once count === maxQuantity, so this only fires
      // when adding is allowed or when the cart is over a (just-dropped) cap.
      const newCount = Math.max(this.count + this.step, 1)
      this.set(newCount)

      posthog.capture('added_incremental_cart_item', {
        ...getCommonTrackingProperties(this),
        count: newCount
      })
    },
    setQuantity(value) {
      const quantity = parseInt(value, 10) || 0
      posthog.capture('added_cart_item_via_input', {
        ...getCommonTrackingProperties(this),
        count: quantity
      })
      this.set(quantity)
    },
    set(quantity) {
      if (quantity > 0) {
        this.handleTracking(quantity)
      }
      const updatedItem = Object.assign({}, this.submittableCartItem, { quantity })
      if (this.v2UpdateCartItem) {
        this.v2UpdateCartItem(updatedItem)
      } else {
        this.$store.dispatch('updateSubmittableCart', { submittableCartItem: updatedItem })
      }
    },
    handleTracking(quantity) {
      window.bottle.hooks.v1.emit('add_to_cart', {
        items: [
          {
            item_id: this.product.id,
            item_name: this.product.name,
            quantity
          }
        ]
      })
      if (this.facebookPixelContainerID) {
        window.fbq('track', 'AddToCart', {
          product_name: this.product?.name,
          quantity: quantity,
          product_id: this.product?.id
        })
      }
    }
  }
}
</script>
